import { lazy, Suspense, useEffect } from "react";
/// Components
import Index from "./jsx/router/index";
import { connect, useDispatch } from "react-redux";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";
// action
import { checkAutoLogin } from "./services/AuthService";
import { isAuthenticated } from "./store/selectors/AuthSelectors";

/// Style
import "rsuite/dist/rsuite-no-reset.min.css";
import "./assets/css/style.css";
import "./assets/css/globalStyle.css";
import "./assets/css/publicPageStyle.css";
import ProtectedRoute from "./jsx/protector/ProtectedRoute";
import SuperAdmin from "./jsx/router/super-admin";
import PublicDashboard from "./jsx/router/public";
import CoachDashboard from "./jsx/router/coach";
import StaffDashboard from "./jsx/router/staff";
import PlayerDashboard from "./jsx/router/player";
import AdminDashboard from "./jsx/router/admin";
import TenantAdminDashboard from "./jsx/router/tenant-admin";
import ForgetPassword from "./jsx/pages/authentication/ForgetPassword";
import EmailVerification from "./jsx/pages/public-page/EmailVerification";

const SignUp = lazy(() => import("./jsx/pages/authentication/Registration"));
const Login = lazy(() => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(import("./jsx/pages/authentication/Login")), 500);
  });
});

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();

    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}

function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    checkAutoLogin(dispatch, navigate);
  }, [dispatch, navigate]);

  // Role-based redirect component
  const RoleBasedRedirect = () => {
    const role = props.role;

    if (!role) {
      return <Navigate to="/login" replace />;
    }

    switch (role) {
      case "admin":
        return <Navigate to="/admin/dashboard" replace />;
      case "tenant-admin":
        return (
          <Navigate to="/tenant-admin/registered-academies-list" replace />
        );
      case "super-admin":
        return <Navigate to="/super-admin/dashboard" replace />;
      case "player":
        return <Navigate to="/player/payment-history" replace />;
      case "coach":
        return <Navigate to="/coach/attendance" replace />;
      case "staff":
        return <Navigate to="/staff/dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  const loadingScreen = (
    <div id="preloader">
      <div className="sk-three-bounce">
        <div className="sk-child sk-bounce1"></div>
        <div className="sk-child sk-bounce2"></div>
        <div className="sk-child sk-bounce3"></div>
      </div>
    </div>
  );

  return (
    <div className="vh-100">
      <Suspense fallback={loadingScreen}>
        {!props.authChecked ? (
          loadingScreen
        ) : (
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/page-register" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgetPassword />} />
            <Route path="/email-verification" element={<EmailVerification />} />

            <Route
              path="/public/*"
              element={
                // <ProtectedRoute>
                <PublicDashboard />
                // </ProtectedRoute>
              }
            />

            {/* Role-based protected routes */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tenant-admin/*"
              element={
                <ProtectedRoute allowedRoles={["tenant-admin"]}>
                  <TenantAdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/super-admin/*"
              element={
                <ProtectedRoute allowedRoles={["super-admin"]}>
                  <SuperAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/player/*"
              element={
                <ProtectedRoute allowedRoles={["player"]}>
                  <PlayerDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/*"
              element={
                <ProtectedRoute allowedRoles={["coach"]}>
                  <CoachDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/staff/*"
              element={
                <ProtectedRoute allowedRoles={["staff"]}>
                  <StaffDashboard />
                </ProtectedRoute>
              }
            />

            {/* Root redirect - automatically redirect to role-based dashboard */}
            <Route
              path="/"
              element={
                props.isAuthenticated ? (
                  <RoleBasedRedirect />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Fallback for any other routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </Suspense>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
    authChecked: state.auth.authChecked,
    role: state.auth.role,
  };
};

export default withRouter(connect(mapStateToProps)(App));
